import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  Image as ChakraImage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { FaPencilAlt, FaTrash, FaUpload, FaDownload } from "react-icons/fa";
import Cropper from "react-easy-crop";
import { storage } from "lib/firebase";
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from "@firebase/storage";
import { userProfile } from "contexts/userProfile";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Signature() {
  const [signatures, setSignatures] = useState([]);
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isDrawModalOpen, setDrawModalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const uploadToFirebase = async (blob, baseFilename) => {
    const userId = userProfile.get().data?.uid;
    if (!userId) {
      console.error("No user ID found. Cannot save to Firebase.");
      return;
    }
    try {
      const uniqueFilename = `${baseFilename.split(".")[0]}_${Date.now()}.${
        baseFilename.split(".")[1]
      }`;

      const storageRef = ref(storage, `signatures/${userId}/${uniqueFilename}`);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);

      setSignatures((prevSignatures) => {
        return [...prevSignatures, url];
      });

      toast.success("Signature uploaded successfully!");
    } catch (error) {
      console.error("Failed to upload the signature:", error);
      toast.error("Failed to upload the signature. Please try again!");
    }
  };

  const handleUpload = (event) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target.result);
      setIsOpen(true);
    };
    reader.readAsDataURL(event.target.files[0]);
  };

  const handleDelete = async (index) => {
    const signatureUrl = signatures[index];
    const decodedUrl = decodeURIComponent(signatureUrl);
    const userId = userProfile.get().data?.uid;
    const pathParts = decodedUrl.split("/");
    const filename = pathParts[pathParts.length - 1].split("?")[0];

    const storageRef = ref(storage, `signatures/${userId}/${filename}`);
    try {
      await deleteObject(storageRef);

      const updatedSignatures = [...signatures];
      updatedSignatures.splice(index, 1);
      setSignatures(updatedSignatures);
      toast.success("Signature deleted successfully!");
    } catch (err) {
      console.error("Error deleting signature:", err);
      toast.error("Failed to delete the signature. Please try again!");
    }
  };

  const downloadSignature = async (signature, index) => {
    try {
      const response = await fetch(signature);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `signature_${index}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Signature downloaded successfully!");
    } catch (error) {
      console.error("Error downloading signature:", error);
      toast.error("Failed to download the signature. Please try again!");
    }
  };

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const getCroppedImage = async () => {
    const image = new window.Image();
    image.src = imageSrc;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const { x, y, width, height } = croppedAreaPixels;
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(image, x, y, width, height, 0, 0, width, height);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        blob.name = `newImage_${Date.now()}.png`;
        resolve(blob);
      }, "image/png");
    });
  };

  const startDrawing = useCallback((event) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  }, []);

  const drawSignature = useCallback(
    (event) => {
      if (!isDrawing) return;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      ctx.lineTo(x, y);
      ctx.stroke();
    },
    [isDrawing]
  );

  const finishDrawing = useCallback(() => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.closePath();
  }, []);

  const saveDrawing = async () => {
    const canvas = canvasRef.current;
    const signatureImg = canvas.toDataURL("image/png");

    const response = await fetch(signatureImg);
    const blob = await response.blob();

    const timestamp = Date.now();
    blob.name = `drawnImage_${timestamp}.png`;
    await uploadToFirebase(blob, blob.name);

    setDrawModalOpen(false);
    clearCanvas();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const onMouseDown = (event) => {
    startDrawing(event);
    setIsDrawing(true);
  };

  const onMouseMove = (event) => {
    if (!isDrawing) return;
    drawSignature(event);
  };

  const onMouseUp = () => {
    setIsDrawing(false);
    finishDrawing();
  };

  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.lineCap = "round";
      ctx.strokeStyle = "black";
      ctx.lineWidth = 5;

      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mouseup", finishDrawing);
      canvas.removeEventListener("mousemove", drawSignature);

      canvas.addEventListener("mousedown", startDrawing);
      canvas.addEventListener("mouseup", finishDrawing);
      canvas.addEventListener("mousemove", drawSignature);
    }
  };

  useEffect(() => {
    const fetchSignaturesFromFirebase = async () => {
      const userId = userProfile.get().data?.uid;
      if (!userId) return;

      const signaturesRef = ref(storage, `signatures/${userId}`);
      const urls = [];

      const list = await listAll(signaturesRef);
      for (let itemRef of list.items) {
        const url = await getDownloadURL(itemRef);
        urls.push(url);
      }

      setSignatures(urls);
    };

    fetchSignaturesFromFirebase();

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.lineCap = "round";
      ctx.strokeStyle = "black";
      ctx.lineWidth = 5;

      canvas.addEventListener("mousedown", startDrawing);
      canvas.addEventListener("mouseup", finishDrawing);
      canvas.addEventListener("mousemove", drawSignature);

      return () => {
        canvas.removeEventListener("mousedown", startDrawing);
        canvas.removeEventListener("mouseup", finishDrawing);
        canvas.removeEventListener("mousemove", drawSignature);
      };
    }
  }, [startDrawing, finishDrawing, drawSignature]);

  return (
    <Flex
      direction="column"
      pt={{ base: "120px", md: "75px", lg: "100px" }}
      minH="70vh"
      justifyContent="center"
      alignItems="center"
    >
      <Flex mb={6} direction="column" alignItems="center">
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleUpload}
          accept="image/*"
        />

        <Button
          leftIcon={<FaPencilAlt />}
          colorScheme="navy"
          onClick={() => {
            setDrawModalOpen(true);
            initializeCanvas();
          }}
          size="lg"
          mb={4}
        >
          Draw Signature
        </Button>
        <Button
          leftIcon={<FaUpload />}
          colorScheme="green"
          variant="solid"
          onClick={handleUploadClick}
          size="lg"
        >
          Upload Signature
        </Button>
      </Flex>

      {/* Upload and Crop Signature Modal */}
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent mx="auto" w="80%">
          <ModalHeader>Crop Signature</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <div style={{ width: "100%", height: "400px" }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={4 / 1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={async () => {
                const blob = await getCroppedImage();
                await uploadToFirebase(blob, blob.name);
                setIsOpen(false);
              }}
            >
              Save Cropped Image
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Draw Signature Modal */}
      <Modal
        isOpen={isDrawModalOpen}
        onClose={() => {
          setDrawModalOpen(false);
          clearCanvas();
        }}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent mx="auto" w="80%">
          <ModalHeader>Draw Signature</ModalHeader>
          <ModalCloseButton />
          <ModalBody p={4} d="flex" justifyContent="center" alignItems="center">
            {" "}
            {/* Centering the canvas */}
            <canvas
              ref={canvasRef}
              width={540}
              height={135}
              style={{ border: "1px solid black", backgroundColor: "white" }}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
            />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={saveDrawing}>
              Save Signature
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Grid templateColumns={{ sm: "1fr", xl: "repeat(3, 1fr)" }} gap="22px" mt="24px">
        {signatures.map((signature, index) => (
          <Box
            key={index}
            borderRadius="lg"
            borderWidth="1px"
            overflow="hidden"
            p={4}
            position="relative"
            boxShadow="0px 2px 5.5px rgba(0, 0, 0, 0.02)"
            backgroundColor="white"
          >
            <ChakraImage src={signature} alt={`signature-${index}`} />
            <Button
              position="absolute"
              top="4px"
              right="4px"
              size="md"
              colorScheme="transparent"
              leftIcon={<FaTrash />}
              onClick={() => handleDelete(index)}
              textColor={"red"}
            />

            <Button
              position="absolute"
              top="4px"
              left="4px"
              size="md"
              colorScheme="transparent"
              leftIcon={<FaDownload />}
              textColor={"green.400"}
              onClick={() => downloadSignature(signature, index)}
            />
          </Box>
        ))}
      </Grid>
      <ToastContainer />
    </Flex>
  );
}

export default Signature;
